<template>
  <v-container fluid >
    <v-row dense>
      <v-col cols="12" md="4">
        <v-card outlined class="dashboard-card mb-3">
          <v-card-text class="pa-4">
            <div class="card-header">
              <div>
                <div class="section-title">Sales Rep</div>
                <div class="section-subtitle">Assign customer owner</div>
              </div>
            </div>

            <div class="mt-4">
              <SaleRep
                @change="updateSaleman"
                v-model="selectedUser"
                :initValue="getCustomer.SalesRep"
              />
            </div>
          </v-card-text>
        </v-card>

        <v-card outlined class="dashboard-card">
          <v-card-text class="pa-4">
            <div class="card-header">
              <div>
                <div class="section-title">Note</div>
                <div class="section-subtitle">Internal customer note</div>
              </div>

              <div class="note-actions">
                <v-btn
                  v-if="checkRightStatus(36)"
                  small
                  rounded
                  color="primary"
                  :loading="loading"
                  dark
                  @click="updateNote"
                >
                  <v-icon left small>mdi-content-save-outline</v-icon>
                  Save
                </v-btn>
              </div>
            </div>

            <v-textarea
              v-model="note"
              outlined
              dense
              auto-grow
              rows="4"
              hide-details
              label="Customer Note"
              class="mt-4"
            />

            <div v-if="checkRightStatus(37)" class="toggle-box mt-4">
              <div>
                <div class="toggle-title">Show Alert</div>
                <div class="toggle-subtitle">
                  Display this note as a warning on profile.
                </div>
              </div>

              <toggle-button
                @change="updateAlert"
                :labels="true"
                v-if="getCustomer"
                color="red"
                v-model="getCustomer.alert"
                :value="getCustomer.alert"
                :sync="true"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card
          v-if="checkRightStatus(37)"
          outlined
          class="dashboard-card mb-3"
        >
          <v-card-text class="pa-4">
            <div class="toggle-box">
              <div>
                <div class="section-title">Order Control</div>
                <div class="section-subtitle">
                  Block this customer from creating orders.
                </div>
              </div>

              <toggle-button
                @change="updateBlock"
                :labels="true"
                v-if="getCustomer"
                color="red"
                v-model="getCustomer.blockOrder"
                :value="getCustomer.blockOrder"
                :sync="true"
              />
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="checkRightStatus(37)"
          outlined
          class="dashboard-card mb-3"
        >
          <v-card-text class="pa-4">
            <div class="toggle-box email-reminder-control">
              <div>
                <div class="section-title">Due Invoice Emails</div>
                <div class="section-subtitle">
                  Send this customer automated upcoming, due, and past-due invoice reminders.
                </div>
              </div>

              <toggle-button
                @change="updateInvoiceDueEmailReminders"
                :labels="true"
                v-if="getCustomer"
                color="#2563eb"
                :value="invoiceDueEmailRemindersEnabled"
                :sync="true"
              />
            </div>
            <div
              v-if="!invoiceDueEmailRemindersEnabled"
              class="email-reminder-disabled mt-3"
            >
              <v-icon small color="orange darken-2" class="mr-1">
                mdi-email-off-outline
              </v-icon>
              Automated due-invoice emails are disabled for this customer.
            </div>
          </v-card-text>
        </v-card>

        <Comment :RelationId="getCustomer" :TypeId="2" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import customerService from "../service";

const Comment = () => import("@/modules/Comment/components/List");
import SaleRep from "@/modules/User/components/AutoComplete.vue";

export default {
  name: "customer-dashboard",

  components: {
    Comment,
    SaleRep,
  },

  data() {
    return {
      loading: false,
      note: null,
      selectedUser: null,
    };
  },

  computed: {
    ...mapGetters("customer", ["getCustomer"]),
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
    invoiceDueEmailRemindersEnabled() {
      const metadata = this.getCustomer && this.getCustomer.metadata;
      return !metadata || metadata.invoiceDueEmailReminders !== false;
    },
  },

  watch: {
    getCustomer: {
      immediate: true,
      handler(val) {
        this.note = val ? val.note : null;
      },
    },
  },

  methods: {
    ...mapActions("customer", ["setCustomer"]),

    updateNote() {
      const data = {
        ...this.getCustomer,
        note: this.note,
      };

      this.updateCustomer(data);
    },

    updateAlert(val) {
      const data = {
        ...this.getCustomer,
        alert: val.value,
      };

      this.updateCustomer(data);
    },

    updateBlock(val) {
      const data = {
        ...this.getCustomer,
        blockOrder: val.value,
      };

      this.updateCustomer(data);
    },

    updateInvoiceDueEmailReminders(val) {
      const data = {
        ...this.getCustomer,
        metadata: {
          ...(this.getCustomer.metadata || {}),
          invoiceDueEmailReminders: val.value,
        },
      };

      this.updateCustomer(data);
    },

    updateSaleman(val) {
      if (!val || !val.id) {
        return;
      }

      const data = {
        ...this.getCustomer,
        SalesRepUserId: val.id,
      };

      this.updateCustomer(data);
    },

    async updateCustomer(data) {
      if (!this.getCustomer || !this.getCustomer.id) {
        return;
      }

      this.loading = true;

      try {
        await customerService.update(this.getCustomer.id, data);

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Updated successfully",
          });

        await this.setCustomer(this.getCustomer.id);
        this.$events.emit("CustomerMiniList");
      } catch (error) {
        const message =
          error && error.data && error.data.message
            ? error.data.message
            : "Unable to update customer";

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: message,
          });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.customer-dashboard-page {
  background: #f8fafc;
}

.dashboard-card {
  border-radius: 20px;
  background: #ffffff;
}

.card-header,
.toggle-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.email-reminder-control {
  border: 1px solid #e2e8f0;
}

.email-reminder-disabled {
  padding: 10px 12px;
  border-radius: 12px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 12px;
  font-weight: 700;
}

.section-title,
.toggle-title {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.section-subtitle,
.toggle-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.note-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

@media (max-width: 600px) {
  .card-header,
  .toggle-box {
    align-items: flex-start;
  }
}
</style>
